/**
 * Servicio para consultar pedidos HomeCenter y sincronizar por fecha y tipoOrden
 */
import axiosInstance from '../../api/axiosInstance'
import type { Order } from '../../types/order'
import type { SyncByDateResponse } from '../../types/cron/homecenterManual'
import { normalizeHomeCenterSyncByDateResponse } from '../../utils/homeCenterSyncUtils'

interface ListResponse {
  data: Order[]
  meta: {
    currentPage: number
    totalPages: number
    totalItems: number
    statistics?: any
  }
}

export class HomeCenterManualService {
  private static readonly TARGET_STORE = 'Bigcenter'
  private static readonly TARGET_STATE = 'enviado'
  private static readonly LIMIT_PER_PAGE = 100
  private static readonly TIPO_ORDENES = [1, 4, 8]

  /**
   * Obtener todos los pedidos HomeCenter en estado "enviado"
   * Consulta todas las páginas necesarias
   */
  static async getAllHomeCenterOrders(
    onProgress?: (progress: {
      currentPage: number
      totalPages: number
      ordersFetched: number
    }) => void
  ): Promise<Order[]> {
    const token = localStorage.getItem('accessToken')
    if (!token) {
      throw new Error('No se encontró token de autenticación')
    }

    const allOrders: Order[] = []
    let currentPage = 1
    let totalPages = 1

    try {
      // Primera consulta para obtener totalPages
      const firstResponse = await axiosInstance.get<ListResponse>(
        `/list?page=${currentPage}&limit=${this.LIMIT_PER_PAGE}&states=${this.TARGET_STATE}&stores=${this.TARGET_STORE}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )

      const firstData = firstResponse.data.data
      totalPages = firstResponse.data.meta.totalPages

      allOrders.push(...firstData)

      // Notificar progreso
      if (onProgress) {
        onProgress({
          currentPage: 1,
          totalPages,
          ordersFetched: firstData.length,
        })
      }

      // Si hay más páginas, consultarlas todas
      if (totalPages > 1) {
        for (let page = 2; page <= totalPages; page++) {
          const response = await axiosInstance.get<ListResponse>(
            `/list?page=${page}&limit=${this.LIMIT_PER_PAGE}&states=${this.TARGET_STATE}&stores=${this.TARGET_STORE}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          )

          const pageData = response.data.data
          allOrders.push(...pageData)

          // Notificar progreso
          if (onProgress) {
            onProgress({
              currentPage: page,
              totalPages,
              ordersFetched: allOrders.length,
            })
          }
        }
      }

      return allOrders
    } catch (error: any) {
      console.error('Error obteniendo pedidos HomeCenter:', error)
      throw new Error(
        error.response?.data?.message ||
          `Error al obtener pedidos: ${error.message}`
      )
    }
  }

  /**
   * Calcular fecha inicial (pedido más viejo - 2 días)
   */
  static calculateStartDate(orders: Order[]): Date {
    if (orders.length === 0) {
      throw new Error('No hay pedidos para calcular la fecha inicial')
    }

    // Encontrar la fecha más vieja
    const dates = orders
      .map((order) => new Date(order.date_created).getTime())
      .filter((time) => !isNaN(time))

    if (dates.length === 0) {
      throw new Error('No se encontraron fechas válidas en los pedidos')
    }

    const oldestTimestamp = Math.min(...dates)
    const oldestDate = new Date(oldestTimestamp)

    // Restar 2 días
    const startDate = new Date(oldestDate)
    startDate.setDate(startDate.getDate() - 2)

    return startDate
  }

  /**
   * Generar array de fechas desde fecha inicial hasta hoy
   * Formato: YYYY-MM-DD
   */
  static generateDateRange(startDate: Date, endDate: Date = new Date()): string[] {
    const dates: string[] = []
    const current = new Date(startDate)

    // Asegurar que endDate sea hoy
    const today = new Date(endDate)
    today.setHours(23, 59, 59, 999)

    while (current <= today) {
      const dateStr = current.toISOString().split('T')[0] // YYYY-MM-DD
      dates.push(dateStr)
      current.setDate(current.getDate() + 1)
    }

    return dates
  }

  /**
   * Sincronizar por fecha y tipoOrden
   */
  static async syncByDate(
    fecha: string,
    tipoOrden: number
  ): Promise<SyncByDateResponse> {
    try {
      const response = await axiosInstance.post<SyncByDateResponse>(
        '/homecenter/sync-pending-by-date',
        {
          fecha,
          tipoOrden,
        }
      )

      return normalizeHomeCenterSyncByDateResponse(response.data, fecha)
    } catch (error: any) {
      console.error(`Error sincronizando fecha ${fecha} con tipoOrden ${tipoOrden}:`, error)
      throw new Error(
        error.response?.data?.message ||
          `Error al sincronizar: ${error.message}`
      )
    }
  }

  /**
   * Procesar todas las fechas para un tipoOrden específico
   */
  static async processAllDates(
    dates: string[],
    tipoOrden: number,
    onProgress?: (progress: {
      currentDate: string
      processedDates: number
      totalDates: number
      currentResult?: SyncByDateResponse
    }) => void
  ): Promise<SyncByDateResponse[]> {
    const results: SyncByDateResponse[] = []

    for (let i = 0; i < dates.length; i++) {
      const fecha = dates[i]

      try {
        const result = await this.syncByDate(fecha, tipoOrden)
        results.push(result)

        // Notificar progreso
        if (onProgress) {
          onProgress({
            currentDate: fecha,
            processedDates: i + 1,
            totalDates: dates.length,
            currentResult: result,
          })
        }

        // Pequeño delay entre fechas para evitar sobrecarga
        if (i < dates.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500))
        }
      } catch (error: any) {
        console.error(`Error procesando fecha ${fecha}:`, error)
        // Continuar con la siguiente fecha aunque esta falle
        // Agregar resultado de error para mantener consistencia
        results.push({
          success: false,
          message: `Error al procesar fecha ${fecha}: ${error.message}`,
          results: {
            fecha,
            total: 0,
            created: 0,
            skipped: 0,
            errors: 1,
            detalles: [],
          },
        })

        if (onProgress) {
          onProgress({
            currentDate: fecha,
            processedDates: i + 1,
            totalDates: dates.length,
          })
        }
      }
    }

    return results
  }

  /**
   * Obtener lista de tipoOrdenes
   */
  static getTipoOrdenes(): number[] {
    return [...this.TIPO_ORDENES]
  }
}

